import React, { useState, useMemo } from 'react';
import { Wand2, Plus, X, LayoutGrid } from 'lucide-react';

const claseCampo = 'w-full h-9 px-2.5 border border-gray-300 rounded-lg text-sm tabular-nums bg-white focus:ring-2 focus:ring-blue-500 outline-none';
const MAXIMO = 200;

const ordenar = (lista) => [...lista].sort((a, b) => a.pos - b.pos);

// Posiciones de una máquina de tipo propio. Se generan por filas (fila 1 =
// 11, 12…; fila 2 = 21, 22…), se copian de un tipo existente o se agregan
// una a una, y luego se ajusta la capacidad de cada una. Punto V las crea
// con existencia 0.
const EditorPosiciones = ({ value, onChange, tipos }) => {
  const [gen, setGen] = useState({ filas: '3', porFila: '5', capacidad: '8', minimo: '2' });
  const [nueva, setNueva] = useState('');
  const [aviso, setAviso] = useState('');

  const cambiarGen = (campo, v) => setGen(prev => ({ ...prev, [campo]: v }));
  const totalGenerar = (parseInt(gen.filas, 10) || 0) * Math.min(parseInt(gen.porFila, 10) || 0, 9);

  const generar = () => {
    const filas = Math.min(Math.max(parseInt(gen.filas, 10) || 0, 1), 20);
    const porFila = Math.min(Math.max(parseInt(gen.porFila, 10) || 0, 1), 9);
    const capacidad = Math.max(parseInt(gen.capacidad, 10) || 1, 1);
    const minimo = Math.min(Math.max(parseInt(gen.minimo, 10) || 0, 0), capacidad);
    const lista = [];
    for (let f = 1; f <= filas; f += 1) {
      for (let k = 1; k <= porFila; k += 1) lista.push({ pos: f * 10 + k, minimo_maq: minimo, maximo_maq: capacidad });
    }
    setAviso('');
    onChange(lista);
  };

  const copiarDe = (tipo) => {
    const origen = tipos.find(t => t.tipo === tipo);
    if (!origen) return;
    setAviso('');
    onChange(origen.posiciones.map(p => ({ pos: p.pos, minimo_maq: p.minimo_maq, maximo_maq: p.maximo_maq })));
  };

  const agregar = () => {
    const pos = parseInt(nueva, 10);
    if (!(pos >= 1 && pos <= 999)) return setAviso('La posición debe ser un número entre 1 y 999.');
    if (value.some(p => p.pos === pos)) return setAviso(`La posición ${pos} ya existe.`);
    if (value.length >= MAXIMO) return setAviso(`Máximo ${MAXIMO} posiciones.`);
    const capacidad = Math.max(parseInt(gen.capacidad, 10) || 1, 1);
    onChange(ordenar([...value, { pos, minimo_maq: Math.min(parseInt(gen.minimo, 10) || 0, capacidad), maximo_maq: capacidad }]));
    setNueva('');
    setAviso('');
  };

  const quitar = (pos) => onChange(value.filter(p => p.pos !== pos));

  const ajustarCapacidad = (pos, v) => onChange(value.map(p => {
    if (p.pos !== pos) return p;
    const maximo = Math.max(parseInt(v, 10) || 1, 1);
    return { ...p, maximo_maq: maximo, minimo_maq: Math.min(p.minimo_maq, maximo) };
  }));

  const grupos = useMemo(() => {
    const porDecena = new Map();
    ordenar(value).forEach((p) => {
      const d = Math.floor(p.pos / 10);
      if (!porDecena.has(d)) porDecena.set(d, []);
      porDecena.get(d).push(p);
    });
    return [...porDecena.entries()];
  }, [value]);

  const capacidadTotal = value.reduce((acc, p) => acc + Number(p.maximo_maq), 0);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <LayoutGrid size={16} className="text-blue-600" />
        <h4 className="text-sm font-semibold text-gray-800">Posiciones</h4>
        {value.length > 0 && (
          <span className="ml-auto text-xs text-gray-500 tabular-nums">
            {value.length} posiciones · capacidad {capacidadTotal}
          </span>
        )}
      </div>

      <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            ['filas', 'Filas', 1, 20],
            ['porFila', 'Por fila', 1, 9],
            ['capacidad', 'Capacidad', 1, undefined],
            ['minimo', 'Mínimo', 0, undefined]
          ].map(([campo, etiqueta, min, max]) => (
            <label key={campo} className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">{etiqueta}</span>
              <input
                type="number"
                min={min}
                max={max}
                value={gen[campo]}
                onChange={(e) => cambiarGen(campo, e.target.value)}
                className={claseCampo}
              />
            </label>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={generar}
            className="h-9 px-3 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 flex items-center gap-1.5"
          >
            <Wand2 size={15} /> Generar {totalGenerar || ''} posiciones
          </button>
          {tipos.length > 0 && (
            <select
              value=""
              onChange={(e) => copiarDe(e.target.value)}
              aria-label="Copiar posiciones de un tipo existente"
              className="h-9 px-2.5 border border-gray-300 rounded-lg text-sm bg-white text-gray-600 focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="">o copiar de un tipo…</option>
              {tipos.map(t => <option key={t.tipo} value={t.tipo}>{t.tipo} ({t.posiciones.length})</option>)}
            </select>
          )}
        </div>
        <p className="text-xs text-gray-400">Fila 1 = posiciones 11, 12…; fila 2 = 21, 22… (hasta 9 por fila).</p>
      </div>

      <div className="px-4 py-3">
        {value.length > 0 ? (
          <>
            <p className="text-xs text-gray-400 mb-2">Ajuste la capacidad de cada posición. Pase el cursor para quitar una.</p>
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {grupos.map(([decena, posiciones]) => (
                <div key={decena} className="flex items-start gap-3">
                  <span className="w-12 flex-shrink-0 pt-2.5 text-xs text-gray-400 tabular-nums">{decena * 10}–{decena * 10 + 9}</span>
                  <div className="flex-1 grid grid-cols-[repeat(auto-fill,minmax(4.5rem,1fr))] gap-2">
                    {posiciones.map(p => (
                      <div key={p.pos} className="group relative rounded-lg border border-gray-200 bg-white p-1.5 hover:border-blue-300 focus-within:border-blue-400">
                        <div className="flex items-center justify-between px-0.5">
                          <span className="text-sm font-semibold text-gray-800 tabular-nums">{p.pos}</span>
                          <button
                            type="button"
                            onClick={() => quitar(p.pos)}
                            aria-label={`Quitar posición ${p.pos}`}
                            className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-400 hover:text-red-600"
                          >
                            <X size={13} />
                          </button>
                        </div>
                        <input
                          type="number"
                          min="1"
                          value={p.maximo_maq}
                          onChange={(e) => ajustarCapacidad(p.pos, e.target.value)}
                          aria-label={`Capacidad posición ${p.pos}`}
                          title="Capacidad"
                          className="mt-1 w-full h-7 px-1.5 border border-gray-200 rounded-md text-sm text-center tabular-nums bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-400 text-center py-6">Genere las posiciones o cópielas de un tipo existente.</p>
        )}

        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
          <input
            type="number"
            min="1"
            max="999"
            value={nueva}
            onChange={(e) => setNueva(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregar(); } }}
            placeholder="N.º"
            aria-label="Número de posición a agregar"
            className="w-24 h-9 px-2.5 border border-gray-300 rounded-lg text-sm tabular-nums focus:ring-2 focus:ring-blue-500 outline-none"
          />
          <button type="button" onClick={agregar} className="h-9 px-2 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 whitespace-nowrap">
            <Plus size={15} /> Agregar posición
          </button>
          {value.length > 0 && (
            <button type="button" onClick={() => onChange([])} className="ml-auto text-sm text-gray-400 hover:text-red-600">
              Vaciar
            </button>
          )}
        </div>
        {aviso && <p className="text-xs text-red-600 mt-2">{aviso}</p>}
        <p className="text-xs text-gray-400 mt-2">Se crean con existencia 0 y el producto genérico; el producto real se asigna en Punto V Manager.</p>
      </div>
    </div>
  );
};

export default EditorPosiciones;
